import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import imgLeaveYourMark from '../assets/images/tutorial-leave-your-mark.png';
import TcImage from '../components/TcImage';
import { useSimpleImagePreloader } from '../hooks/useImagePreloader';
import LoadingScreen from '../components/shared/LoadingScreen';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

export default function LeaveYourMark() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';

    const [isGettingReady, setIsGettingReady] = useState(true);

    // Use the custom hook for scroll detection
    const isAtBottom = useScrollToBottom({
        containerSelector: '.main-content-wrapper'
    });
    

    // Preload all images when component mounts
    const allImages = [
        imgLeaveYourMark
    ];

    const { isLoading } = useSimpleImagePreloader(allImages);

    // Force refresh function using window.location.reload() for consistency
    const forceRefresh = () => {
        console.log('🔄 Force refreshing LeaveYourMark component...');
        
        // Check if we've already attempted a refresh to prevent infinite loops
        const hasRefreshedForLeaveYourMark = sessionStorage.getItem('leaveYourMarkRefreshAttempted');
        
        if (!hasRefreshedForLeaveYourMark) {
            // Mark that we've attempted a refresh
            sessionStorage.setItem('leaveYourMarkRefreshAttempted', 'true');
            
            // Small delay before refresh to allow component to mount properly
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } else {
            // If we've already refreshed, just mark as ready
            setTimeout(() => {
                setIsGettingReady(false);
                console.log('✅ LeaveYourMark component ready');
            }, 1000);
        }
    };
    
    const handleProceed = () => {
        setTimeout(() => {
            navigate('/');
        }, 10);
    };

    useEffect(() => {
        // Force refresh on component mount
        console.log('🚀 LeaveYourMark component mounted - performing force refresh');
        forceRefresh();
    }, []);
    

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading..." />;
    }

    
    return (
        <div className={`
            main-content-wrapper !pb-24 bg-image-galaxy 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-6 mb-4">
                <h1 className="main-content-title"
                    dangerouslySetInnerHTML={{ __html: t('leaveYourMark.title') }}
                />
            </TcPaper>
            
            <TcPaper classNameStyle="general-content">
                <p className="mb-4"
                    dangerouslySetInnerHTML={{ __html: t('leaveYourMark.description') }}
                />

                {imgLeaveYourMark ? (
                    <TcImage classNameStyle={`
                        mx-auto mb-2 max-w-xl 
                        w-2/3 2xs:w-full xs:w-2/3 sm:w-2/3 md:w-2/3 lg:w-1/2 
                    `}>
                        <img src={imgLeaveYourMark} alt="Leave your mark" className="relative w-full h-full object-cover" />
                    </TcImage>
                ) : (
                    <></>
                )}
                
                <div className={`button-wrapper stick-to-bottom p-4 !bottom-[4vw] !max-w-[92vw] has-transition ${
                    isAtBottom ? 'clear-transparent' : 'glossy-translucent-with-border'
                }`}>
                    <TcButton 
                        handleClick={isGettingReady ? undefined : handleProceed}
                        primary
                        disabled={isGettingReady}
                    >
                        {isGettingReady ? t('common.gettingReady') : t('common.letsDoThis')}
                    </TcButton>
                </div>
            </TcPaper>
        </div>
    );
} 